import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

const RELOAD_FLAG = 'sm_chunk_reload';

/** Returns true when the error looks like a stale/failed dynamic import (chunk). */
function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch/i.test(msg);
}

/**
 * App-wide error boundary. Prevents a single page crash from blanking the whole
 * app, and auto-recovers from stale chunk loads after a deployment by reloading
 * once (guarded against reload loops).
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();
    }
  }

  handleReload = (): void => {
    sessionStorage.removeItem(RELOAD_FLAG);
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
          <h2
            className="text-xl font-semibold text-[#1A2332] mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Something went wrong
          </h2>
          <p className="text-sm text-[#6B7280] max-w-md mb-4">
            This page failed to load. Reloading usually fixes it.
          </p>
          <button
            onClick={this.handleReload}
            className="px-4 py-2 rounded-lg bg-[#C08B5C] text-white text-sm font-medium hover:bg-[#A87749] transition-colors"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
